import React, { useEffect, useState } from 'react';
import './styles.scss';

import {
  IAPIProtractedFieldConfig,
  IAutoTable,
  IConfigField,
  IListItem,
  IValue,
} from './model';
import { IString } from '../../model';
import { WrapperComponent } from './tables/wrapper';
import {
  getFilterValue,
  getProtractedFieldConfigData,
  isAggregationRenderScene,
  log,
  mergeFieldConfigOptExtension,
  protractedDataMergeBySort,
  triggerFieldLinkageChanged,
  getProtractedFontSizeConfigData,
  setProtractedFontSizeConfigData,
} from './libs';
import classNames from 'classnames';
import { TableFontSize } from '../table';
import { FilterGroup } from '../filter-group';

export const AutoTable = <T,>(props: IAutoTable<T>) => {
  const { layoutExtension, configFilter, list = [] } = props;

  const protractedFontSize = props?.protractedExtension?.fontSize;

  const [filterValues, setFilterValues] = useState<IString<IValue>>(
    props.filterValues || {}
  );
  const [filterList, setFilterList] = useState<readonly T[]>(props.list || []);

  const [configFields, setConfigFields] = useState<
    ReadonlyArray<IConfigField<T>>
  >([]);

  const defaultTableSize = props.tableExtension?.size || TableFontSize.Small;

  const [tableSize, setTableSize] = useState<TableFontSize>(defaultTableSize);

  const [tableSizeInit, setTableSizeInit] = useState<boolean>(false);

  const isAggregationScene = isAggregationRenderScene(
    props.layoutExtension?.renderScene
  );

  const initConfigFields = (configFields: ReadonlyArray<IConfigField<T>>) => {
    const json: IString<IConfigField<T>> = {};
    configFields.forEach((field) => {
      json[field.key] = field;
    });

    configFields.forEach((field, index) => {
      mergeFieldConfigOptExtension(
        field,
        (fieldConfig) => {
          const { key } = fieldConfig;
          if (!key) {
            return;
          }

          // json[key] = fieldConfig;
          const value = getFilterValue(fieldConfig, filterValues);
          fieldLinkageChanged(json, configFields, key, value);

          const templateFields = configFields.slice();
          templateFields.splice(index, 1, fieldConfig);
          combinationSetConfigFields(templateFields);
        },
        true,
        props.cacheFilterValues
      );
    });
  };

  const combinationSetConfigFields = (
    configFields: ReadonlyArray<IConfigField<T>>
  ) => {
    setConfigFields(configFields);
  };

  // 处理字段关联网更新
  const fieldLinkageChanged = (
    jsonConfigFields: IString<IConfigField<T>>,
    configFields: ReadonlyArray<IConfigField<T>>,
    key: string,
    value: any
  ) => {
    triggerFieldLinkageChanged(
      jsonConfigFields,
      configFields,
      key,
      value,
      (fieldConfig: IConfigField<T>, targetIndex: number) => {
        const templateFields = configFields.slice();
        templateFields.splice(targetIndex, 1, fieldConfig);
        combinationSetConfigFields(templateFields);
      }
    );
  };

  // Table body编辑
  const onTableBodyChanged = (
    key: string,
    value: any,
    row: IListItem,
    field: IConfigField<T>,
    index: number
  ) => {
    if (props.onTableBodyChanged) {
      props.onTableBodyChanged(key, value, row, field, index);
    }
  };

  const onChangedSize = (size: TableFontSize) => {
    setTableSize(size);

    if (protractedFontSize?.isEnable && protractedFontSize?.guid) {
      setProtractedFontSizeConfigData(
        size,
        (status: boolean) => {
          if (!status) {
            log.warn('Table font size failed to store');
          }
        },
        props.protractedExtension
      );

      if (props?.onChangedFontSize) {
        props.onChangedFontSize(size);
      }
    }
  };

  // 对数据进行持久化判断，并初始化字段配置规则
  const initProtractedFieldConfig = () => {
    getProtractedFieldConfigData<IAPIProtractedFieldConfig>((data) => {
      const sortData = protractedDataMergeBySort(data, props.configFields);
      initConfigFields(sortData);
    }, props.protractedExtension);
  };

  // 初始化获取字体大小配置
  const initProtractedFontSizeConfig = async () => {
    // 开启了配置存在guid就获取值
    if (protractedFontSize?.isEnable && protractedFontSize?.guid) {
      getProtractedFontSizeConfigData((size: TableFontSize | undefined) => {
        const fontSize = size || defaultTableSize;
        setTableSize(fontSize);
        setTableSizeInit(true);
        if (props?.onInitFontSize) {
          props.onInitFontSize(fontSize);
        }
      }, props.protractedExtension);
    } else {
      setTableSizeInit(true);
    }
  };

  // 获取存储的fontsize完成后，后续fontsize更新以设置的为准
  useEffect(() => {
    const propsSize = props?.tableExtension?.size;
    if (propsSize && tableSizeInit && propsSize !== tableSize) {
      onChangedSize(propsSize);
    }
  }, [props?.tableExtension?.size]);

  useEffect(() => {
    initProtractedFontSizeConfig();
  }, []);

  useEffect(() => {
    if (props.configFields.length) {
      initProtractedFieldConfig();
    }
  }, [props.configFields]);

  useEffect(() => {
    if (props.filterValues) {
      setFilterValues(props.filterValues);
    }
  }, [props.filterValues]);

  const renderTopbar = () => {
    if (configFilter) {
      return (
        <FilterGroup
          style={{ marginBottom: 16 }}
          values={configFilter.values}
          filterItems={{ list: props.list || [], items: configFilter.filters }}
          onFilterChange={(_val, _values, filterList) => {
            setFilterList(filterList || []);
            configFilter.onChange?.(_val, _values, filterList);
            const rowKey = props.tableExtension?.rowKey || 'id';
            const allRowKeys = filterList.map(
              (item) => (item as Record<string, any>)[rowKey]
            );
            const selectedRowKeys =
              props.tableExtension?.rowSelection?.selectedRowKeys || [];

            const filterKeys = (selectedRowKeys as any[]).filter((key) => {
              return allRowKeys.includes(key);
            });

            const filterRows = filterList.filter((row) => {
              return filterKeys.includes((row as Record<string, any>)[rowKey]);
            });

            props.tableExtension?.rowSelection?.onChange?.(
              filterKeys,
              filterRows,
              undefined,
              false,
              false
            );
          }}
        />
      );
    }
    return null;
  };

  const renderTable = () => {
    if (layoutExtension?.showTable === false) {
      return null;
    }
    const tableExtendsion = Object.assign({}, props.tableExtension, {
      size: tableSize,
    });
    return (
      <WrapperComponent
        configFields={configFields}
        list={configFilter ? filterList : list}
        loading={props.loading || false}
        onChanged={onTableBodyChanged}
        renderAllRow={props.renderAllRow}
        tableExtension={tableExtendsion}
        filterValues={filterValues}
      />
    );
  };

  const renderContent = () => {
    return (
      <>
        {renderTopbar()}
        {layoutExtension?.renderTableTopSpace}
        {renderTable()}
      </>
    );
  };

  const className = classNames('auto-table-box', props.className, {
    'aggregation-scene': isAggregationScene,
  });
  const style: React.CSSProperties = Object.assign({}, props.style);
  return (
    <div className={className} style={style}>
      {renderContent()}
    </div>
  );
};
